import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import CoreTable from '../../coreComponents/Table/Table.tsx';
import { getDriversToVerify } from '../../services/DriverService.ts';
import { TDriverListData } from './type.ts';
import DriverProfileModal from './DriverDetails.tsx';
import UpdateDriver from './UpdateDriver.tsx';
import {
  Verified,
  CancelOutlined,
  PendingActions,
  CloudUpload,
  ErrorOutline,
  Clear
} from '@mui/icons-material';
import { Box, Tooltip, Typography, Dialog, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import Button from '../../coreComponents/Button/Button.tsx';
import { SelectChangeEvent } from '@mui/material';
import debounce from 'lodash/debounce';

const Status = ({ status, showTooltip = true }) => {
  // Configuration for each status type
  const statusConfig = {
    'VERIFIED': {
      icon: <Verified sx={{ color: 'success.main' }} />,
      tooltip: 'Verified',
      color: 'success.main'
    },
    'REJECTED': {
      icon: <CancelOutlined sx={{ color: 'error.main' }} />,
      tooltip: 'Rejected',
      color: 'error.main'
    },
    'VERIFICATION_PENDING': {
      icon: <PendingActions sx={{ color: 'warning.main' }} />,
      tooltip: 'Pending Verification',
      color: 'warning.main'
    },
    'UPLOADED': {
      icon: <CloudUpload sx={{ color: 'info.main' }} />,
      tooltip: 'Uploaded (Verification Not Initiated)',
      color: 'info.main'
    },
    'PENDING_UPLOAD': {
      icon: <ErrorOutline sx={{ color: 'text.disabled' }} />,
      tooltip: 'Not Uploaded',
      color: 'text.disabled'
    }
  };

  // Default/fallback config for unknown status
  const defaultConfig = {
    icon: <ErrorOutline sx={{ color: 'text.disabled' }} />,
    tooltip: 'Unknown Status',
    color: 'text.disabled'
  };

  // Get config for the current status or use default
  const config = statusConfig[status] || defaultConfig;

  const icon = config.icon;

  // Return with or without tooltip based on props
  if (showTooltip) {
    return (
      <Tooltip title={config.tooltip} arrow>
        <Box component="span" display="inline-flex" alignItems="center">
          {icon}
        </Box>
      </Tooltip>
    );
  }

  return icon;
};

const VerifiedStatus = ({ verificationDetails }) => {
  if (!verificationDetails) return null;

  const items = [
    { label: 'RC', value: verificationDetails.rc_status },
    { label: 'License', value: verificationDetails.license_status },
    { label: 'License No.', value: verificationDetails.license_number_status },
    { label: 'Photo', value: verificationDetails.profile_photo_status }
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        minWidth: 'fit-content'
      }}
    >
      {items.map(({ label, value }) => (
        <Box
          key={label}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            whiteSpace: 'nowrap'
          }}
        >
          <Typography variant="body2" component="span">
            {label}:
          </Typography>
          <Status status={value} />
        </Box>
      ))}
    </Box>
  );
};


const columns = [
  { id: 'id', label: 'ID' },
  { id: 'name', label: 'Name', searchable: true },
  { id: 'phone', label: 'Phone', searchable: true },
  {
    id: 'verificationStatus',
    label: 'Status',
    render: (value) => <VerifiedStatus verificationDetails={value} />,
    width: '400px',
  },
  { id: 'email', label: 'Email', searchable: true },
  {
    id: 'created_at',
    label: 'Application Date',
    render: (value) => new Date(value).toLocaleDateString(),
  },
  {
    id: 'updated_at',
    label: 'Last Updated',
    render: (value) => new Date(value).toLocaleDateString(),
  }
];

const DriverToVerify: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<TDriverListData[]>([]);
  const [allData, setAllData] = useState<TDriverListData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

  const [id, setId] = useState<number>();
  const [open, setOpen] = useState(false);
  const [openUpdate, setOpenUpdate] = useState(false);

  // Memoize the filter function
  const filterData = useCallback((data: TDriverListData[], status: string | null) => {
    if (!status) return data;
    
    return data.filter(driver => {
      const verificationDetails = driver.verificationStatus;
      return (
        verificationDetails.rc_status === status ||
        verificationDetails.license_status === status ||
        verificationDetails.license_number_status === status ||
        verificationDetails.profile_photo_status === status
      );
    });
  }, []);

  // Debounced filter function
  const debouncedFilter = useMemo(
    () =>
      debounce((data: TDriverListData[], status: string | null) => {
        const filteredData = filterData(data, status);
        setData(filteredData);
      }, 300),
    [filterData]
  );

  const filterAndSetData = useCallback((data: TDriverListData[], status: string | null) => {
    debouncedFilter(data, status);
  }, [debouncedFilter]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedFilter.cancel();
    };
  }, [debouncedFilter]);

  // Memoize the filtered data
  const filteredData = useMemo(() => {
    return filterData(allData, selectedStatus);
  }, [allData, selectedStatus, filterData]);

  useEffect(() => {
    const fetchDriverData = async () => {
      try {
        const data = (await getDriversToVerify()) || [];
        const list: TDriverListData[] = data?.data?.data;

        if (list) {
          const sortedList = [...list].sort((a, b) => 
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
          );
          setAllData(sortedList);
          setData(sortedList);
        }
      } catch (err: any) {
        if (err.response && err.response.status === 401) {
          console.error('Unauthorized access. Redirecting to login...');
          navigate('/login');
        } else {
          setError('Failed to fetch drivers');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDriverData();
  }, [navigate, refreshKey]);

  // Effect for handling status changes
  useEffect(() => {
    filterAndSetData(allData, selectedStatus);
  }, [selectedStatus, allData, filterAndSetData]);

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    setSelectedStatus(event.target.value || null);
  };

  const handleClearStatus = () => {
    setSelectedStatus(null);
  };

  const viewDetails = (id: number) => {
    setId(id);
    setOpen(true);
  };

  const updateDriverDetails = (id: number) => {
    setId(id);
    setOpenUpdate(true);
  };

  const handleCreateDriver = () => {
    navigate('/create-driver');
  };

  const handleCloseUpdate = () => {
    setOpenUpdate(false);
    setId(undefined);
  };

  const handleUpdateSuccess = () => {
    setOpenUpdate(false);
    setId(undefined);
    setRefreshKey(prev => prev + 1);
  };

  const handleProfileClose = () => {
    setOpen(false);
    setRefreshKey(prev => prev + 1);
  };

  if (loading && data.length === 0) return <div>Loading unverified drivers...</div>;
  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <Box sx={{ pt: 0, pb: 3, px: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginBottom: '24px',
          backgroundColor: '#fff',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          minWidth: 300, 
          alignItems: 'center',
          '& .MuiFormControl-root': {
            minWidth: 200
          }
        }}>
          <FormControl size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={selectedStatus || ''}
              onChange={handleStatusChange}
              label="Status"
              sx={{
                height: '36px',
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center'
                }
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="VERIFIED">Verified</MenuItem>
              <MenuItem value="REJECTED">Rejected</MenuItem>
              <MenuItem value="VERIFICATION_PENDING">Pending Verification</MenuItem>
              <MenuItem value="UPLOADED">Uploaded</MenuItem>
              <MenuItem value="PENDING_UPLOAD">Pending Upload</MenuItem>
            </Select>
          </FormControl>
          {selectedStatus && (
            <Button
              label="Clear"
              onClick={handleClearStatus}
              variant="primary"
              style={{
                height: '36px',
                minWidth: '80px'
              }}
            />
          )}
        </Box>
      </Box>
      <CoreTable
        columns={columns}
        data={filteredData}
        onUpdate={viewDetails}
        label1="Verify Documents"
        showSearch={false}
      />
      <DriverProfileModal
        driverId={id}
        open={open}
        onClose={handleProfileClose}
      />
      <Dialog 
        open={openUpdate} 
        onClose={handleCloseUpdate}
        maxWidth="md"
        fullWidth
      >
        {id && (
          <UpdateDriver 
            id={id.toString()} 
            onClose={handleCloseUpdate}
            onUpdateClose={handleUpdateSuccess}
          />
        )}
      </Dialog>
    </Box>
  );
};

export default DriverToVerify;
